
import React, { useState } from 'react';

interface Pillar {
  tag: string;
  title: string;
  desc: string;
  color: string;
  image: string;
  details: string[];
}

const Pillars: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const pillars: Pillar[] = [
    {
      tag: "PWA",
      title: "Sistemas de gestión a medida",
      desc: "Diseñamos plataformas web y apps personalizadas para gestionar ventas, stock, turnos, clientes y procesos internos. Se adapta a tu rubro real.",
      color: "from-blue-500 to-cyan-500",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800",
      details: [
        "Gestión de Ventas y Facturación",
        "Control de Stock en tiempo real",
        "Sistemas de Turnos Inteligentes",
        "Operaciones internas optimizadas"
      ]
    },
    {
      tag: "E-Commerce",
      title: "Tiendas Online & E-Commerce",
      desc: "E-commerce propias y de Shopify, catálogos online y landing pages pensadas para convertir visitas en clientes desde el primer día.",
      color: "from-purple-500 to-pink-500",
      image: "https://images.unsplash.com/photo-1557821552-17105176677c?auto=format&fit=crop&q=80&w=800",
      details: [
        "E-commerce a medida y Shopify",
        "Catálogos digitales interactivos",
        "Integración de pagos y envíos",
        "Landing Pages de alta conversión"
      ]
    },
    {
      tag: "Legacy",
      title: "De Excel a sistema real",
      desc: "Transformamos tus planillas y Excels en sistemas profesionales con paneles claros, reportes y trabajo colaborativo.",
      color: "from-orange-500 to-red-500",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800",
      details: [
        "Migración de datos de Excel a DB",
        "Dashboards de control intuitivos",
        "Reportes automáticos",
        "Acceso colaborativo seguro"
      ]
    },
    {
      tag: "IA & Workflow",
      title: "Automatización de procesos",
      desc: "Alertas, mensajes y reportes automáticos que te devuelven tiempo. Tu negocio funcionando de forma más eficiente.",
      color: "from-green-500 to-emerald-500",
      image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&q=80&w=800",
      details: [
        "Automatización de tareas repetitivas",
        "Alertas y notificaciones automáticas",
        "Flujos de trabajo inteligentes",
        "Reducción de errores manuales"
      ]
    },
    {
      tag: "Diseño",
      title: "Diseño Web Profesional",
      desc: "Sitios web institucionales con diseño UX/UI de elite que transmite confianza y tecnología. Imagen profesional para marcas que buscan liderar.",
      color: "from-yellow-500 to-orange-500",
      image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&q=80&w=800",
      details: [
        "Interfaces UX/UI Premium",
        "Webs Corporativas Escalables",
        "Prototipado de alta fidelidad",
        "Consistencia de marca digital"
      ]
    },
    {
      tag: "Kiosks",
      title: "Kioscos digitales interactivos",
      desc: "Implementamos tótems con trivias, encuestas y recolección de datos para eventos y locales comerciales. Impacto garantizado.",
      color: "from-red-600 to-red-400",
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=800",
      details: [
        "Aplicaciones para tótems",
        "Experiencias gamificadas",
        "Captación de leads en eventos",
        "Sistemas de info interactiva"
      ]
    }
  ];

  const toggleAccordion = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section id="pilar" className="py-24 bg-gradient-to-b from-transparent to-[#0a0a0a]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-black text-gradient mb-6">Servicios Principales</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Combinamos diseño UX/UI de alto nivel con ingeniería de software robusta para entregar soluciones que realmente impactan en el balance de tu empresa.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {pillars.map((p, i) => {
            const isExpanded = expandedIndex === i;
            return (
              <div 
                key={i} 
                className={`group relative overflow-hidden rounded-[2.5rem] bg-white/5 border transition-all duration-500 ease-out cursor-default flex flex-col h-full ${
                  isExpanded ? 'border-white/40 shadow-2xl shadow-white/10 ring-1 ring-white/10' : 'border-white/10 hover:border-white/30 hover:scale-[1.02]'
                }`}
              >
                <div className="p-8 relative z-10 flex flex-col h-full">
                  <div className={`inline-block px-4 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-6 bg-gradient-to-r ${p.color} text-white w-fit`}>
                    {p.tag}
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{p.title}</h3>
                  <p className="text-gray-400 mb-6 leading-relaxed text-sm">
                    {p.desc}
                  </p>
                  
                  {/* Accordion Trigger */}
                  <button 
                    onClick={() => toggleAccordion(i)}
                    className="flex items-center gap-4 text-white font-bold transition-all duration-300 w-fit focus:outline-none mb-4"
                  >
                    {isExpanded ? 'Ver menos' : 'Ver detalles'}
                    <span className={`w-8 h-8 rounded-full border border-white/20 flex items-center justify-center transition-all duration-500 ${
                      isExpanded ? 'bg-white text-black rotate-180' : 'group-hover:bg-white group-hover:text-black'
                    }`}>
                      {isExpanded ? '−' : '→'}
                    </span>
                  </button>

                  {/* Accordion Content */}
                  <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isExpanded ? 'max-h-[300px] opacity-100 mb-6' : 'max-h-0 opacity-0'
                  }`}>
                    <ul className="space-y-3 pt-4 border-t border-white/10">
                      {p.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-300 group/item">
                          <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 bg-gradient-to-r ${p.color}`} />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Image Section */}
                <div className={`h-48 w-full overflow-hidden mt-auto transition-all duration-700 ${
                  isExpanded ? 'h-32 opacity-20 filter blur-sm' : 'opacity-40 group-hover:opacity-100'
                }`}>
                  <img 
                    src={p.image} 
                    alt={p.title} 
                    className={`w-full h-full object-cover transition-all duration-1000 ${
                      isExpanded ? 'scale-125' : 'grayscale group-hover:grayscale-0 scale-110 group-hover:scale-100'
                    }`} 
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pillars;
